import React, { FC, memo, useCallback, useMemo, useState } from "react";

// components
import { ErrorBoundary } from "@swiggy-private/core-ui";
import { OneBlckPlanRemoveConfirmationBottomsheet } from "@swiggy-private/dls-web-recipe-common-web/dist/components/SwiggyOne";
import { PlanRemoveConfirmationBottomsheetWrapper } from "@super-ui/components/cart-upsell-nudge/Components/PlanRemoveConfirmationBottomsheetWrapper";

// types
import { SubscriptionRenewalNudgeProps } from "./types";

// utils
import { getNRErrorInfo, isEmpty } from "@swiggy-private/js-utils";
import { isSwiggyBlack } from "@super-ui/components/swiggy-one-widget-dls-wrapper/helpers";
import SubscriptionRenewalNudgeWidget from "./components/SubscriptionRenewalNudgeWidget";

// hooks
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { useGetRemoveBSProps } from "@super-ui/components/cart-upsell-nudge/hooks/useGetRemoveBSProps";

const SubscriptionRenewalNudge: FC<SubscriptionRenewalNudgeProps> = (props) => {
    const { comparisonNudgeComms, analyticsEvents, handlers, removePlanMessage, planDetails, isTopPositioned } = props;
    const { subscriptionRenewalAnalytics } = analyticsEvents;

    const [showRemovePlanConfirmation, setShowRemovePlanConfirmation] = useState(false);

    const { bottomSheetProps, allowPlanRemovalBottomsheet } = useGetRemoveBSProps({
        removePlanMessage,
        handlers: { ...handlers, setShowRemovePlanConfirmation, nudgeAnalytics: subscriptionRenewalAnalytics },
        planDetails,
        showRemovePlanConfirmation,
    });

    const isSwiggyBlackPlan = isSwiggyBlack(planDetails);

    const PlanRemoveBottomsheet = useMemo(
        () =>
            isSwiggyBlackPlan ? (
                <OneBlckPlanRemoveConfirmationBottomsheet {...bottomSheetProps} />
            ) : (
                <PlanRemoveConfirmationBottomsheetWrapper
                    removePlanMessage={removePlanMessage}
                    bottomSheetProps={bottomSheetProps}
                    planDetails={planDetails}
                />
            ),
        [bottomSheetProps, isSwiggyBlackPlan, removePlanMessage, planDetails],
    );

    const onError = useCallback(
        (error?: Error, errorInfo?: React.ErrorInfo) => {
            const errorEvents = getNRErrorInfo(error, errorInfo);

            recordNREvent({
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
                widgetType: "subscription-renewal-nudge",
                ...errorEvents,
                nudgeComms: JSON.stringify({ comparisonNudgeComms }),
            });
        },
        [comparisonNudgeComms],
    );

    if (isEmpty(comparisonNudgeComms) || isEmpty(comparisonNudgeComms.nudges)) {
        return null;
    }

    return (
        <ErrorBoundary errorContent={null} errorFallback={onError}>
            <SubscriptionRenewalNudgeWidget
                comparisonNudgeComms={comparisonNudgeComms}
                handlers={handlers}
                analyticsEvents={subscriptionRenewalAnalytics}
                setShowRemovePlanConfirmation={setShowRemovePlanConfirmation}
                isTopPositioned={isTopPositioned}
            />
            {allowPlanRemovalBottomsheet && PlanRemoveBottomsheet}
        </ErrorBoundary>
    );
};

export default memo(SubscriptionRenewalNudge);
